import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import API from '@/lib/api';
import SignupWizard from './SignupWizard';

export default function Auth({ mode = 'login' }) {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  // Handle Google auth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) handleGoogleCallback(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const res = await API.post('/auth/google/callback', { session_id: sessionId });
      if (res.data.token) {
        loginWithToken(res.data.token, res.data.user);
        toast.success('Signed in with Google!');
        const u = res.data.user;
        if (u && u.profile_completed === false) {
          navigate('/complete-profile');
        } else {
          navigate(u.role === 'creator' ? '/creator' : u.role === 'admin' ? '/admin' : '/agency');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect_url=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(form.email, form.password);
      toast.success('Welcome back!');
      const u = res.user;
      if (u && u.profile_completed === false) navigate('/complete-profile');
      else navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Called by SignupWizard once email is verified.
  // We use a hard navigation here to side-step a React Router re-mount race
  // where the freshly mounted CompleteProfile briefly sees stale auth state
  // and bounces away.
  const handleSignupSuccess = ({ user, token }) => {
    if (token) localStorage.setItem('token', token);
    toast.success('Welcome to Mylikeness!');
    const target = (user && (user.role === 'creator' || user.role === 'buyer' || user.role === 'agency_admin'))
      ? '/complete-profile'
      : '/';
    window.location.href = target;
  };

  return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md">

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-3" data-testid="auth-back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div>
            <Link to="/" className="font-heading text-2xl font-semibold text-[#101828]" data-testid="auth-logo">Mylikeness</Link>
            <p className="text-[#475467] text-sm mt-1">Licensed presence, verified proof</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#D0D5DD] shadow-sm p-8">
          {/* Tab Toggle */}
          <div className="flex mb-6 bg-[#F6F8FC] rounded-xl p-1">
            <button data-testid="auth-login-tab" onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${isLogin ? 'bg-white text-[#101828] shadow-sm' : 'text-[#475467] hover:text-[#101828]'}`}>
              Sign In
            </button>
            <button data-testid="auth-signup-tab" onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${!isLogin ? 'bg-white text-[#101828] shadow-sm' : 'text-[#475467] hover:text-[#101828]'}`}>
              Create Account
            </button>
          </div>

          {isLogin ? (
            <>
              <Button data-testid="google-signin-btn" type="button" variant="outline"
                onClick={handleGoogleLogin}
                className="w-full h-11 mb-4 border-[#D0D5DD] hover:bg-[#F6F8FC] text-[#101828] font-medium">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#D0D5DD]"></div></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#475467]">or</span></div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-[#101828] text-sm font-medium mb-1.5 block">Email</Label>
                  <Input data-testid="auth-email-input" type="email" placeholder="you@example.com" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white border-[#D0D5DD] h-11" required />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-[#101828] text-sm font-medium block">Password</Label>
                    <Link to="/forgot-password" data-testid="forgot-password-link"
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input data-testid="auth-password-input" type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password" value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="bg-white border-[#D0D5DD] h-11 pr-10" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475467] hover:text-[#101828]">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
                  className="w-full btn-primary h-11 text-sm font-medium">
                  {loading ? 'Please wait…' : 'Sign In'}
                </Button>
              </form>
            </>
          ) : (
            <SignupWizard
              onSuccess={handleSignupSuccess}
              onSwitchToLogin={() => setIsLogin(true)}
            />
          )}
        </div>

        <p className="text-center text-[#475467] text-xs mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </motion.div>
    </div>
  );
}
